// src/pages/investmentFunds/dashboard/Summary.jsx

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { selectors as investmentfundsSelectors } from "reducers/investmentfunds";
import DonutChart from "./DonutChart";
import "../../../styles/components/modules/_summary.scss";

const Summary = ({ currency, dollarRate }) => {
    // 1) Traer datos crudos de Redux
    const depositsAll = useSelector((s) => investmentfundsSelectors.getDeposits(s)) || [];
    const fciAll = useSelector((s) => investmentfundsSelectors.getFci(s)) || [];
    const bacAll = useSelector((s) => investmentfundsSelectors.getBac(s)) || [];

    // 2) Calcular totales por categoría según pestaña:
    //    - en "ARS" sumamos todos los amountTransfered en pesos
    //    - en "USD" solo los que tengan currency === 'USD' (si los trae tu API)
    const { totalDeposits, totalFci, totalBac, totalSum } = useMemo(() => {
        let deps = 0;
        let fcis = 0;
        let bacs = 0;

        if (currency === "ARS") {
            // todos los depósitos son ARS
            deps = depositsAll.reduce((sum, d) => sum + (d.amountTransfered || 0), 0);

            // FCI: sumo enPesos de todas las especies de la primera cuenta
            fcis = (fciAll[0]?.listaPorEspecie || []).reduce((sum, i) => sum + (i.saldo.enPesos || 0), 0);

            // BAC: idem
            bacs = (bacAll[0]?.listaPorEspecie || []).reduce((sum, i) => sum + (i.saldo.enPesos || 0), 0);
        } else {
            // USD: asumo que tu API marca currency o que tienes un campo enDolares
            deps = depositsAll
                .filter((d) => d.currency === "USD")
                .reduce((sum, d) => sum + (d.amountTransfered || 0), 0);

            // FCI en USD: si tu objeto tiene un campo .saldo.enDolares, o lo conviertes:
            fcis = (fciAll[0]?.listaPorEspecie || []).reduce((sum, i) => {
                // ejemplo: i.saldo.enDolares || (i.saldo.enPesos/dollarRate)
                const val = i.saldo.enDolares != null ? i.saldo.enDolares : (i.saldo.enPesos || 0) / dollarRate;
                return sum + val;
            }, 0);

            // BAC en USD
            bacs = (bacAll[0]?.listaPorEspecie || []).reduce((sum, i) => {
                const val = i.saldo.enDolares != null ? i.saldo.enDolares : (i.saldo.enPesos || 0) / dollarRate;
                return sum + val;
            }, 0);
        }

        const total = deps + fcis + bacs;
        return {
            totalDeposits: deps,
            totalFci: fcis,
            totalBac: bacs,
            totalSum: total,
        };
    }, [depositsAll, fciAll, bacAll, currency, dollarRate]);

    // 3) Armar chartData: solo categorías con total > 0
    const chartData = useMemo(() => {
        if (totalSum === 0) {
            return [{ name: "Sin datos", value: 100, color: "#8E8E8E" }];
        }
        const slices = [];
        if (totalDeposits > 0) {
            slices.push({ name: "Plazos Fijos", value: totalDeposits, color: "#4FA097" });
        }
        if (totalFci > 0) {
            slices.push({ name: "Fondos Comunes de Inversión", value: totalFci, color: "#35EEC8" });
        }
        if (totalBac > 0) {
            slices.push({ name: "Bonos, Acciones y CEDEARs", value: totalBac, color: "#000000" });
        }
        return slices;
    }, [totalDeposits, totalFci, totalBac, totalSum]);

    // 4) Convertir a porcentajes para el DonutChart
    const pctData = useMemo(() => {
        if (chartData.length === 1 && chartData[0].name === "Sin datos") {
            return chartData;
        }
        return chartData.map((slice) => ({
            ...slice,
            value: (slice.value / totalSum) * 100,
        }));
    }, [chartData, totalSum]);

    // 5) Helper para formatear montos
    const format = (amount) =>
        amount.toLocaleString("es-AR", {
            style: "currency",
            currency: currency === "ARS" ? "ARS" : "USD",
            minimumFractionDigits: 2,
        });

    const isEmpty = totalSum === 0;

    return (
        <div className={`summary-container above-the-fold ${isEmpty ? "card--disabled" : ""}`}>
            <div className="summary-list">
                <div className="summary-list__chart">
                    <DonutChart data={pctData} className="donut" />
                    <div className="summary-donut__label">
                        {format(totalSum)}
                        {isEmpty && <div className="summary-donut__empty">Sin inversiones</div>}
                    </div>
                </div>
                {!isEmpty && (
                    <ul className="summary-list__legend">
                        {chartData.map(({ name, color }) => (
                            <li key={name} className="summary-list__legend-item" style={{ "--bullet-color": color }}>
                                {name}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

Summary.propTypes = {
    currency: PropTypes.oneOf(["ARS", "USD"]).isRequired,
    dollarRate: PropTypes.number.isRequired,
};

export default Summary;
